import React, { useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router'
import styles from './index.module.scss'
import HomeIcon from '../../assets/icon/HomeIcon'
import FolderIcon from '../../assets/icon/FolderIcon'
import SettingsIcon from '../../assets/icon/SettingsIcon'
import PeopleIcon from '../../assets/icon/PeopleIcon'
import { PROJECTS_PAGE_URI } from '../../constants/uri.constant'
import DropDown from '../DropDown'
import WorkspaceModal from '../Modal/components/WorkspaceModal'
import AddMemberModal from '../AddMemberModal'
import { useWorkspace, Workspace } from '../../context/workspace.context'
import WorkspaceRequiredModal from '../WorkspaceRequiredModal'

// The sidebar is permanently collapsed: no top logo, no collapse control.
const collapsed = true

export default function Sidebar() {
    const location = useLocation()
    const navigate = useNavigate()
    const { workspace, workspaces, setWorkspace } = useWorkspace()
    const [workspaceModal, setWorkspaceModal] = useState(false)
    const [inviteModal, setInviteModal] = useState(false)
    const [workspaceRequiredOpen, setWorkspaceRequiredOpen] = useState(false)
    const [openParticipantsAfterWorkspaceCreate, setOpenParticipantsAfterWorkspaceCreate] = useState(false)
    const [dropdownOpen, setDropdownOpen] = useState(false)

    const isProjectsActive = location.pathname.startsWith('/projects')
    const isProjectPage = /^\/projects\/[^/]+\/?$/.test(location.pathname)
    const isParticipantsActive = location.pathname.startsWith('/participants')

    const getInitial = (name: string) => {
        // Skip leading brackets/punctuation like "[DEV] ..." so the avatar shows a letter
        const trimmed = name.replace(/^[^0-9A-Za-zА-Яа-яЁёІіЇїЄєҐґ]+/, '')
        return (trimmed || name).charAt(0).toUpperCase()
    }

    const handleWorkspaceSwitch = (w: Workspace) => {
        setDropdownOpen(false)

        if (isProjectPage) {
            // Unmount the project page before changing the workspace. Otherwise
            // its workspace-sync effect can switch us straight back to the
            // workspace that owns the project being closed.
            navigate(PROJECTS_PAGE_URI, {
                flushSync: true,
                state: { workspaceId: w.id },
            })
        }

        setWorkspace(w)
    }

    const handleParticipantsNavigate = () => {
        if (!workspace?.id) {
            setWorkspaceRequiredOpen(true)
            setDropdownOpen(false)
            return
        }

        navigate(`/participants/${workspace.id}`)
        setDropdownOpen(false)
    }

    const handleWorkspacesNavigate = () => {
        navigate('/workspaces')
        setDropdownOpen(false)
    }

    const workspaceMenu = (
        <div className={styles.sidebar__workspaceMenu}>
            <div className={styles.sidebar__workspaceMenuHeader}>
                <div className={styles.sidebar__workspaceMenuAvatar}>
                    {workspace ? getInitial(workspace.name) : 'Р'}
                </div>
                <div className={styles.sidebar__workspaceMenuInfo}>
                    <button
                        className={styles.sidebar__workspaceMenuName}
                        type="button"
                        title={workspace?.name}
                        onClick={handleParticipantsNavigate}
                    >
                        {workspace?.name || 'Рабочее пространство'}
                    </button>
                    <span className={styles.sidebar__workspaceMenuMembers}>
                        {workspace?.members_count ?? 0} участников
                    </span>
                </div>
            </div>

            <div className={styles.sidebar__workspaceMenuItems}>
                <div
                    className={styles.sidebar__workspaceMenuItem}
                    onClick={handleWorkspacesNavigate}
                >
                    <SettingsIcon />
                    <span>Настройки</span>
                </div>
                <div
                    className={styles.sidebar__workspaceMenuItem}
                    onClick={() => {
                        setInviteModal(true)
                        setDropdownOpen(false)
                    }}
                >
                    <span className={styles.sidebar__workspaceMenuItemPeopleIcon}>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" clipRule="evenodd" d="M10 2.5C10.4602 2.5 10.8333 2.8731 10.8333 3.33333V16.6667C10.8333 17.1269 10.4602 17.5 10 17.5C9.53976 17.5 9.16667 17.1269 9.16667 16.6667V3.33333C9.16667 2.8731 9.53976 2.5 10 2.5Z" fill="currentColor" />
                            <path fillRule="evenodd" clipRule="evenodd" d="M2.5 10C2.5 9.53976 2.8731 9.16667 3.33333 9.16667H16.6667C17.1269 9.16667 17.5 9.53976 17.5 10C17.5 10.4602 17.1269 10.8333 16.6667 10.8333H3.33333C2.8731 10.8333 2.5 10.4602 2.5 10Z" fill="currentColor" />
                        </svg>
                    </span>
                    <span>Пригласить участников</span>
                </div>
            </div>

            {workspaces.length > 1 && (
                <>
                    <div className={styles.sidebar__workspaceMenuDivider} />
                    <span className={styles.sidebar__workspaceSwitchLabel}>
                        Переключить пространство
                    </span>
                    <div className={styles.sidebar__workspaceSwitchList}>
                        {workspaces
                            .filter((w) => String(w.id) !== String(workspace?.id))
                            .map((w) => (
                                <div
                                    key={w.id}
                                    className={styles.sidebar__workspaceSwitchItem}
                                    onClick={() => handleWorkspaceSwitch(w)}
                                >
                                    <div className={styles.sidebar__workspaceMenuAvatar}>
                                        {getInitial(w.name)}
                                    </div>
                                    <div className={styles.sidebar__workspaceMenuInfo}>
                                        <span className={styles.sidebar__workspaceMenuNameLabel} title={w.name}>
                                            {w.name}
                                        </span>
                                        <span className={styles.sidebar__workspaceSwitchMembers}>
                                            {w.members_count ?? 0} участников
                                        </span>
                                    </div>
                                </div>
                            ))}
                    </div>
                </>
            )}

            <button
                className={styles.sidebar__workspaceMenuNew}
                onClick={() => setWorkspaceModal(true)}
            >
                <span className={styles.sidebar__workspaceMenuNewIcon}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" clipRule="evenodd" d="M7 0C7.55228 0 8 0.447715 8 1V13C8 13.5523 7.55228 14 7 14C6.44772 14 6 13.5523 6 13V1C6 0.447715 6.44772 0 7 0Z" fill="#453CFB" />
                        <path fillRule="evenodd" clipRule="evenodd" d="M0 7C0 6.44771 0.447715 6 1 6L13 6C13.5523 6 14 6.44772 14 7C14 7.55229 13.5523 8 13 8H1C0.447715 8 0 7.55228 0 7Z" fill="#453CFB" />
                    </svg>
                </span>
                <span>Новое пространство</span>
            </button>
        </div>
    )

    return (
        <>
            <div className={`${styles.sidebar} ${styles.sidebarCollapsed}`}>
                <div className={styles.sidebar__workspace}>
                    <button
                        className={styles.sidebar__workspaceName}
                        type="button"
                        onClick={handleParticipantsNavigate}
                    >
                        {workspace?.name || 'Рабочее пространство'}
                    </button>
                    <div className={styles.sidebar__workspaceDropdown}>
                        <DropDown
                            closeDropDownByPick
                            setIsOpen={setDropdownOpen}
                            position={collapsed ? 'side-right' : 'right'}
                            dropdownClassName={styles.sidebar__workspaceDropdownPanel}
                            contentClassName={styles.sidebar__workspaceDropdownContent}
                            btn={collapsed ? (
                                <button className={styles.sidebar__workspaceCompactBtn} type="button" aria-label="Workspace menu">
                                    <span className={styles.sidebar__workspaceCompactAvatar}>
                                        {workspace ? getInitial(workspace.name) : 'Р'}
                                    </span>
                                    <span className={styles.sidebar__workspaceCompactArrow}>
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                            style={{
                                                transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                                transition: 'transform 0.2s ease',
                                            }}
                                        >
                                            <path
                                                d="M4 6L8 10L12 6"
                                                stroke="currentColor"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </span>
                                </button>
                            ) : (
                                <button className={styles.sidebar__workspaceArrowBtn} type="button">
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        style={{
                                            transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                            transition: 'transform 0.2s ease',
                                        }}
                                    >
                                        <path
                                            d="M4 6L8 10L12 6"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </button>
                            )}
                        >
                            {workspaceMenu}
                        </DropDown>
                    </div>
                </div>

                <div className={styles.sidebar__divider} />

                <nav className={styles.sidebar__nav}>
                    <NavLink
                        to="/home"
                        className={() =>
                            `${styles.sidebar__navItem} ${location.pathname === '/home' ? styles.sidebar__navItemActive : ''}`
                        }
                    >
                        <span className={styles.sidebar__navIcon}>
                            <HomeIcon />
                        </span>
                        <span className={styles.sidebar__navLabel}>Домой</span>
                    </NavLink>

                    <NavLink
                        to="/projects"
                        className={() =>
                            `${styles.sidebar__navItem} ${isProjectsActive ? styles.sidebar__navItemActive : ''}`
                        }
                    >
                        <span className={styles.sidebar__navIcon}>
                            <FolderIcon />
                        </span>
                        <span className={styles.sidebar__navLabel}>Проекты</span>
                    </NavLink>

                    <button
                        type="button"
                        onClick={handleParticipantsNavigate}
                        className={`${styles.sidebar__navItem} ${isParticipantsActive ? styles.sidebar__navItemActive : ''}`}
                    >
                        <span className={styles.sidebar__navIcon}>
                            <PeopleIcon />
                        </span>
                        <span className={styles.sidebar__navLabel}>Участники</span>
                    </button>
                </nav>
            </div>
            {workspaceModal && (
                <WorkspaceModal
                    onClose={() => {
                        setWorkspaceModal(false)
                        setOpenParticipantsAfterWorkspaceCreate(false)
                    }}
                    onCreated={(createdWorkspace) => {
                        if (openParticipantsAfterWorkspaceCreate && createdWorkspace?.id) {
                            navigate(`/participants/${createdWorkspace.id}`)
                        }
                    }}
                />
            )}
            <WorkspaceRequiredModal
                isOpen={workspaceRequiredOpen}
                feature="participants"
                onClose={() => setWorkspaceRequiredOpen(false)}
                onCreateWorkspace={() => {
                    setWorkspaceRequiredOpen(false)
                    setOpenParticipantsAfterWorkspaceCreate(true)
                    setWorkspaceModal(true)
                }}
            />
            <AddMemberModal isOpen={inviteModal} onClose={() => setInviteModal(false)} />
        </>
    )
}
